import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Plus, Check } from "lucide-react";
import { TAG_SUGGESTIONS, TAG_STYLE } from "./TagChips";

export const TagEditor = ({ clientId, tags = [], onSave, children }) => {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(tags);
  const [custom, setCustom] = useState("");

  useEffect(() => {
    if (open) setLocal(tags || []);
  }, [open, tags]);

  const toggle = (t) =>
    setLocal((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));

  const addCustom = () => {
    const v = custom.trim();
    if (v && !local.includes(v)) setLocal([...local, v]);
    setCustom("");
  };

  const save = () => {
    onSave(clientId, local);
    setOpen(false);
  };

  const allOptions = [...new Set([...TAG_SUGGESTIONS, ...local])];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-64 p-3"
        onPointerDown={(e) => e.stopPropagation()}
        data-testid="tag-editor"
      >
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
          Guest tags
        </p>
        <div className="flex flex-wrap gap-1.5">
          {allOptions.map((t) => {
            const active = local.includes(t);
            return (
              <button
                key={t}
                data-testid={`tag-option-${t}`}
                onClick={() => toggle(t)}
                className={[
                  "flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ring-1 transition-all",
                  active
                    ? (TAG_STYLE[t] || "bg-slate-800 text-white ring-slate-800")
                    : "bg-white text-slate-500 ring-sand-300 hover:ring-slate-400",
                ].join(" ")}
              >
                {active && <Check size={11} />} {t}
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex gap-1.5">
          <Input
            data-testid="tag-custom-input"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCustom()}
            placeholder="Add custom tag"
            className="h-8 text-xs"
          />
          <button
            onClick={addCustom}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-sand-200 text-slate-600 hover:bg-sand-300"
          >
            <Plus size={15} />
          </button>
        </div>
        <button
          data-testid="tag-save-btn"
          onClick={save}
          className="mt-3 h-8 w-full rounded-lg bg-slate-900 text-xs font-semibold text-white hover:bg-slate-800"
        >
          Save tags
        </button>
      </PopoverContent>
    </Popover>
  );
};
